<template>
    <div class="slide-deck-player tw:block tw:relative tw:w-full" style="display: block; position: relative">
        <e-loading :is-full-page="false" :active="isLoading" />
        <div v-if="slides && Array.isArray(slides) && slides.length > 0">
            <!--
                Self-managed fullscreen overlay. We deliberately do NOT use
                vue-fullscreen / the native Fullscreen API: it is unavailable for
                non-video elements on iOS WebKit (Safari and Chrome for iOS), so
                fullscreen either did nothing or rendered under the global nav.
                Teleporting a plain fixed-position div to <body> works everywhere.
            -->
            <teleport to="body" :disabled="!fullscreen">
                <div ref="fullscreen" class="wrapper" :class="fullscreenWrapperClass">
                    <vueper-slides
                        v-if="slides.length > 1"
                        :key="reRender"
                        ref="vueperslides1"
                        touchable
                        disable-arrows-on-edges
                        fade
                        :slide-ratio="slideRatio"
                        :autoplay="false"
                        :bullets="false"
                        arrows
                        @slide="slideAction($event)"
                    >
                        <vueper-slide v-for="(slide, i) in slides" :key="i" :image="slide" class="slide" :class="{ 'fullscreen-slide': fullscreen }" />
                    </vueper-slides>
                    <vueper-slides
                        v-if="slides.length > 1 && !filmstripThumbnails"
                        v-show="!fullscreen || fullScreenNavigation"
                        ref="vueperslides2"
                        class="no-shadow thumbnails"
                        :class="{
                            'thumbnails-two-thirds': slides.length < 8 && slides.length > 4,
                            'thumbnails-half': slides.length <= 4,
                            'thumbnails-one-thirds': slides.length < 3,
                        }"
                        :visible-slides="thumbnailDivisor || 8"
                        :slide-ratio="thumbnailSlideRatio"
                        :bullets="false"
                        :touchable="false"
                        :gap="0.75"
                        :arrows="false"
                        progress
                        @slide="thumbnailAction($event)"
                    >
                        <vueper-slide
                            v-for="(slide, i) in slides"
                            :key="i"
                            :image="slide"
                            @click="$refs.vueperslides2.goToSlide(i)"
                        >
                            <template #content>
                                <span v-if="isViewed(i)" class="thumbnail-viewed-badge" aria-hidden="true">
                                    <e-icon icon="check" />
                                </span>
                            </template>
                        </vueper-slide>
                    </vueper-slides>

                    <!-- Filmstrip thumbnails: left-aligned, fixed-size, horizontal scroll on overflow. -->
                    <div
                        v-else-if="slides.length > 1 && filmstripThumbnails"
                        v-show="!fullscreen || fullScreenNavigation"
                        ref="thumbnailFilmstrip"
                        class="thumbnail-filmstrip"
                    >
                        <button
                            v-for="(slide, i) in slides"
                            :key="i"
                            type="button"
                            class="thumbnail-filmstrip__item"
                            :class="{ 'thumbnail-filmstrip__item--active': i === activeSlideIndex }"
                            :style="{ backgroundImage: `url(${slide})` }"
                            :aria-label="`Go to slide ${i + 1}`"
                            @click="goToSlideFromFilmstrip(i)"
                        >
                            <span v-if="showViewedBadge(i)" class="thumbnail-viewed-badge" aria-hidden="true">
                                <e-icon icon="check" />
                            </span>
                        </button>
                    </div>

                    <e-button class="fullscreen-icon" @click="toggle">
                        <e-icon :icon="fullscreen ? 'compress' : 'expand-arrows-alt'" />
                    </e-button>

                    <e-button
                        v-show="fullscreen === true && slides.length > 1"
                        :type="fullScreenNavigation ? 'is-primary' : ''"
                        class="fullscreen-icon fullscreen-icon-navigation"
                        style="position: absolute"
                        @click="toggleNavigation"
                    >
                        {{ fullScreenNavigation ? 'Hide Slides' : 'Show Slides' }}
                    </e-button>

                    <img
                        v-if="slides.length === 1"
                        width="100%"
                        :src="slideDeck?.thumbnail_signed_url || slides[0]"
                        :alt="slideDeck?.title || 'Slide'"
                    />
                </div>
            </teleport>

            <p
                v-if="trackViewProgress && !completed"
                v-show="!fullscreen"
                class="slide-progress"
                :class="{ 'slide-progress--complete': allSlidesViewed }"
                aria-live="polite"
            >
                {{ slideProgressText }}
            </p>
        </div>
    </div>
</template>

<script>
import Cookies from 'js-cookie';
import { defineComponent } from 'vue';
import { useI18n } from 'vue-i18n';
import { VueperSlide, VueperSlides } from 'vueperslides';
import 'vueperslides/dist/vueperslides.css';

import { initialState, rootDomain } from '@/Helpers.js';
import axios from '@/plugins/axios.js';

export default defineComponent({
    setup() {
        const { t: translate } = useI18n();
        return { translate };
    },

    name: 'SlideDeck',

    components: { VueperSlides, VueperSlide },

    emits: ['viewed-change'],

    props: {
        slideDeckId: {
            type: [String, Number],
            required: true,
        },

        // When true, render thumbnails as a left-aligned, horizontally-scrolling
        // filmstrip instead of the centered paged vueperslides thumbnail slider.
        // Opt-in (corporate); stanbridge keeps the default slider.
        filmstripThumbnails: {
            type: Boolean,
            default: false,
        },

        // When true, track which slides the learner has viewed: show a per-thumbnail viewed
        // badge + a progress caption, persist progress to the backend so it survives reloads,
        // and emit `viewed-change` so the host renderer can gate "Mark Complete". Opt-in
        // (corporate); stanbridge keeps the plain player with no tracking.
        trackViewProgress: {
            type: Boolean,
            default: false,
        },

        // When the host activity is already complete, treat every slide as viewed: show the green
        // check badge on all thumbnails and hide the "Viewed X of Y / All Slides Viewed" caption.
        // Opt-in (corporate passes the activity's completion state); default keeps the live tracking UI.
        completed: {
            type: Boolean,
            default: false,
        },
    },
    data: () => ({
        slides: null,
        fullscreen: false,
        thumbnailDivisor: null,
        imageWidth: null,
        imageHeight: null,
        fullScreenNavigation: true,
        reRender: false,
        thumbnailHeight: 0,
        slideDeck: null,
        activeSlideIndex: 0,
        isLoading: true,
        // Indices of slides the learner has viewed this session (keyed map for reactive lookups).
        viewedSlides: {},
        // Guards the one-time view-tracking bootstrap in the `slides` watcher.
        viewTrackingInitialized: false,
        // Snapshot of the page overflow before this player locked scroll, so we can restore it
        // instead of clobbering another overlay's scroll lock.
        previousOverflow: '',
    }),

    computed: {
        courseId() {
            return this.$route.params.courseId;
        },

        totalSlides() {
            return Array.isArray(this.slides) ? this.slides.length : 0;
        },

        viewedCount() {
            return Object.keys(this.viewedSlides).length;
        },

        allSlidesViewed() {
            return this.totalSlides > 0 && this.viewedCount >= this.totalSlides;
        },

        slideProgressText() {
            if (this.allSlidesViewed) {
                return this.translate('all_slides_viewed');
            }

            return this.translate('slides_viewed_progress', {
                viewed: this.viewedCount,
                total: this.totalSlides,
            });
        },

        activityId() {
            return this.$route.params.activityId;
        },

        hasValidAspectRatio() {
            return this.imageWidth && this.imageHeight && this.imageWidth > 0 && this.imageHeight > 0;
        },

        slideRatio() {
            if (!this.hasValidAspectRatio) {
                return 0.75; // Default 4:3 ratio
            }
            return this.imageHeight / this.imageWidth;
        },

        thumbnailSlideRatio() {
            // We use CSS aspect-ratio: 1/1 on the slides for square thumbnails
            // This value is still needed by vueperslides but won't affect layout
            // since we override padding-bottom: 0 in CSS
            return 1;
        },

        // Fullscreen overlay classes: `wrapper--fullscreen-nav` reserves space for the
        // thumbnail strip so the active slide stays fully visible when "Show Slides" is on.
        fullscreenWrapperClass() {
            return {
                'wrapper--fullscreen': this.fullscreen,
                'wrapper--fullscreen-nav': this.fullscreen && this.fullScreenNavigation,
            };
        },
    },

    watch: {
        // Keep the active filmstrip thumbnail in view when navigating via arrows/keyboard.
        activeSlideIndex() {
            if (this.filmstripThumbnails) {
                this.$nextTick(() => this.scrollFilmstripToActive());
            }
        },

        // Bootstrap view tracking once the slides resolve (covers both the normal fetch path and
        // the local-dev placeholder path). The first slide is visible on load, so count it as
        // viewed; then restore any slides viewed in previous sessions so the gate survives reloads.
        slides(newSlides) {
            if (!this.trackViewProgress || this.viewTrackingInitialized) {
                return;
            }

            if (Array.isArray(newSlides) && newSlides.length > 0) {
                this.viewTrackingInitialized = true;
                this.markViewed(0);
                this.loadViewedProgress();
            }
        },

        slideDeck: {
            handler(newVal) {
                if (newVal && newVal.slide_deck_urls) {
                    this.initializeSlideDeck();
                }
            },
            immediate: true,
        },

        fullscreen(isFullscreen) {
            // Lock background scroll while the overlay is open, snapshotting/restoring the prior
            // value so we don't clobber another overlay's scroll lock.
            if (typeof document !== 'undefined') {
                if (isFullscreen) {
                    this.previousOverflow = document.documentElement.style.overflow;
                    document.documentElement.style.overflow = 'hidden';
                } else {
                    document.documentElement.style.overflow = this.previousOverflow ?? '';
                }
            }

            // Thumbnails start hidden in fullscreen; the "Show Slides" button reveals them.
            this.fullScreenNavigation = !isFullscreen;

            // The slider is teleported to a new container, so re-measure it.
            this.$nextTick(() => {
                this.calculateWidthHeight();
                if (this.activeSlideIndex > 0) {
                    this.$nextTick(() => this.syncSliders(this.activeSlideIndex));
                }
            });
        },
    },

    async created() {
        if (this.courseId && this.activityId) {
            this.logEvent(1);
        }

        this.isLoading = true;

        try {
            // Try to get slide deck from store first
            this.slideDeck = this.$store.getters['SlideDecks/get'](this.slideDeckId);

            // If slide deck doesn't exist in store or doesn't have slide_deck_urls (from list view),
            // fetch the full slide deck data with all URLs
            // Note: List view uses SlideDeckListResource which excludes slide_deck_urls for performance
            if (!this.slideDeck || !this.slideDeck.slide_deck_urls) {
                // Get reference_id from the store data (slideDeckId is numeric id from list view)
                // If slideDeckId is already a reference_id (UUID format), use it directly
                const referenceId =
                    this.slideDeck?.reference_id ||
                    (typeof this.slideDeckId === 'string' && this.slideDeckId.includes('-') ? this.slideDeckId : null);

                if (referenceId) {
                    // Fetch full slide deck data with all URLs using fetchOne (expects reference_id)
                    this.slideDeck = await this.$store.dispatch('SlideDecks/fetchOne', referenceId);
                    // Handle case where fetch succeeded but returned null or incomplete data
                    if (!this.slideDeck || !this.slideDeck.slide_deck_urls) {
                        this.isLoading = false;
                    }
                } else {
                    this.isLoading = false;
                }
            } else {
                // Slide deck is already available with slide_deck_urls, no need to fetch
                // The watcher will handle initialization, but we can set loading to false here
                // since initializeSlideDeck will be called by the watcher
            }
        } catch {
            this.isLoading = false;
        }
    },

    beforeUnmount() {
        document.removeEventListener('keyup', this.changeSlide);
        // Only restore if this instance was the one holding the lock (unmounted while fullscreen).
        if (typeof document !== 'undefined' && this.fullscreen) {
            document.documentElement.style.overflow = this.previousOverflow ?? '';
        }
    },

    methods: {
        initializeSlideDeck() {
            if (!this.slideDeck) {
                this.isLoading = false;
                return;
            }

            // Only set cookies if they exist (CloudFront resolver)
            if (this.slideDeck.slide_deck_cookies) {
                Object.entries(this.slideDeck.slide_deck_cookies).forEach(([key, value]) => {
                    Cookies.set(key, value, { domain: rootDomain(initialState('AppHost')) });
                });
            }

            // Ensure slide_deck_urls is an array
            // Handle both array and object formats (object with numeric keys)
            if (Array.isArray(this.slideDeck.slide_deck_urls)) {
                this.slides = this.slideDeck.slide_deck_urls;
            } else if (this.slideDeck.slide_deck_urls && typeof this.slideDeck.slide_deck_urls === 'object') {
                // Convert object with numeric keys to array
                this.slides = Object.keys(this.slideDeck.slide_deck_urls)
                    .sort((a, b) => Number(a) - Number(b))
                    .map(key => this.slideDeck.slide_deck_urls[key])
                    .filter(url => url != null); // Filter out null/undefined values
            } else if (this.slideDeck.slide_deck_urls) {
                // Single value, wrap in array
                this.slides = [this.slideDeck.slide_deck_urls];
            } else {
                this.slides = [];
            }

            // Mark loading as complete once slides are initialized
            this.isLoading = false;

            // Bootstrap view tracking once, routing through the same one-time guard as the `slides`
            // watcher so we don't double-fire the progress fetch. The first slide is visible on
            // load, so count it as viewed (single-slide decks become fully viewed immediately);
            // then restore any slides viewed in previous sessions so the gate survives reloads
            // (fire-and-forget; progress is advisory).
            if (this.trackViewProgress && !this.viewTrackingInitialized) {
                this.viewTrackingInitialized = true;
                if (this.totalSlides > 0) {
                    this.markViewed(0);
                } else {
                    this.emitViewedChange();
                }
                this.loadViewedProgress();
            }

            // Safely access aspect_ratio with null checks
            if (this.slideDeck.aspect_ratio && Array.isArray(this.slideDeck.aspect_ratio)) {
                [this.imageWidth, this.imageHeight] = this.slideDeck.aspect_ratio;
            } else if (this.slideDeck.aspect_ratio && typeof this.slideDeck.aspect_ratio === 'object') {
                // Handle object format with string keys
                this.imageWidth = this.slideDeck.aspect_ratio['0'] || this.slideDeck.aspect_ratio[0];
                this.imageHeight = this.slideDeck.aspect_ratio['1'] || this.slideDeck.aspect_ratio[1];
            }

            // Force re-render after setting dimensions to ensure vueperslides calculates correctly
            if (this.imageWidth && this.imageHeight) {
                this.$nextTick(() => {
                    this.reRender = !this.reRender;
                });
            }

            // Set up thumbnail divisor for multiple slides
            if (this.slides && Array.isArray(this.slides) && this.slides.length > 1) {
                this.thumbnailDivisor = this.slides.length < 8 ? this.slides.length : 8;
            }

            // Only set up keyboard navigation if we have multiple slides
            if (this.slideDeck.total_slides && Number.parseInt(this.slideDeck.total_slides, 10) > 1) {
                // Remove existing listener to prevent duplicates
                document.removeEventListener('keyup', this.changeSlide);
                document.addEventListener('keyup', this.changeSlide);

                if (this.slides && Array.isArray(this.slides)) {
                    // Ensure both sliders are initialized properly once mounted
                    this.$nextTick(() => {
                        if (this.activeSlideIndex > 0) {
                            this.syncSliders(this.activeSlideIndex);
                        }
                    });
                }
            }
        },

        changeSlide(e) {
            e.preventDefault();
            e.stopPropagation();

            if (e.code === 'Escape') {
                this.fullscreen = false;
            } else if (e.code === 'ArrowRight' || e.code === 'PageDown') {
                if (this.$refs.vueperslides1) {
                    this.$refs.vueperslides1.next();
                }
            } else if (e.code === 'ArrowLeft' || e.code === 'PageUp') {
                if (this.$refs.vueperslides1) {
                    this.$refs.vueperslides1.previous();
                }
            }
        },

        toggle() {
            // Self-managed overlay: just flip the flag. The `fullscreen` watcher
            // teleports the deck to <body> and applies the fullscreen styles.
            this.fullscreen = !this.fullscreen;
        },

        toggleNavigation() {
            this.fullScreenNavigation = !this.fullScreenNavigation;
            // Wait for `wrapper--fullscreen-nav` to apply before re-measuring vueperslides.
            this.$nextTick(() => {
                this.calculateWidthHeight();
            });
        },

        calculateWidthHeight() {
            // Single-slide decks render a plain <img>, no slider to measure.
            if (!this.$refs.vueperslides1) return;

            const element = this.$refs.vueperslides1.$refs.vueperslides;

            element.style.width = '100%';

            this.reRender = !this.reRender;
        },

        slideAction(event) {
            this.activeSlideIndex = event.currentSlide.index;
            this.markViewed(event.currentSlide.index);

            // Only update the thumbnails if they aren't the source of this change
            if (event.origin !== 'thumbnailSync' && this.$refs.vueperslides2) {
                this.$refs.vueperslides2.goToSlide(this.activeSlideIndex, {
                    emit: false,
                });
            }

            if (this.courseId && this.activityId) {
                this.logEvent(event.currentSlide.index + 1);
            }
        },

        thumbnailAction(event) {
            this.activeSlideIndex = event.currentSlide.index;
            this.markViewed(event.currentSlide.index);

            // Only update the main slides if they aren't the source of this change
            if (event.origin !== 'slideSync' && this.$refs.vueperslides1) {
                this.$refs.vueperslides1.goToSlide(this.activeSlideIndex, {
                    emit: false,
                    origin: 'thumbnailSync',
                });
            }

            if (this.courseId && this.activityId) {
                this.logEvent(event.currentSlide.index + 1);
            }
        },

        // Filmstrip thumbnails drive the main slider directly (there is no second
        // vueperslides instance to sync). slideAction keeps activeSlideIndex in step
        // when the user navigates via the arrows/keyboard instead.
        goToSlideFromFilmstrip(index) {
            this.activeSlideIndex = index;
            this.markViewed(index);

            if (this.$refs.vueperslides1) {
                this.$refs.vueperslides1.goToSlide(index, { emit: false, origin: 'thumbnailSync' });
            }

            if (this.courseId && this.activityId) {
                this.logEvent(index + 1);
            }
        },

        scrollFilmstripToActive() {
            const strip = this.$refs.thumbnailFilmstrip;
            const item = strip?.children?.[this.activeSlideIndex];
            item?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
        },

        logEvent(slideNumber) {
            // TODO: Re-enable slide deck event logging after fixing 502 errors
            // Need to implement proper debouncing, request deduplication, and error handling
        },

        isViewed(index) {
            return !!this.viewedSlides[index];
        },

        // Show the green check on a thumbnail when tracking is on and either the learner has viewed
        // that slide or the activity is already complete (completed decks show every slide checked).
        showViewedBadge(index) {
            return this.trackViewProgress && (this.completed || this.isViewed(index));
        },

        markViewed(index) {
            // View tracking is opt-in (corporate). Stanbridge mounts the player without it.
            if (!this.trackViewProgress || index == null || this.isViewed(index)) {
                return;
            }

            this.viewedSlides[index] = true;
            this.persistViewedSlide(index);
            this.emitViewedChange();
        },

        emitViewedChange() {
            this.$emit('viewed-change', {
                viewed: this.viewedCount,
                total: this.totalSlides,
                allViewed: this.allSlidesViewed,
            });
        },

        /**
         * Persist a newly-viewed slide so the completion gate survives reloads. markViewed only
         * fires once per slide, so this posts at most once per slide per session. Outside an
         * activity context (e.g. library preview) there is nothing to persist against.
         */
        persistViewedSlide(index) {
            if (!this.activityId) {
                return;
            }

            axios
                .post(`/api/activities/${this.activityId}/slide-deck-progress`, { slide_number: index + 1 })
                .catch(() => {});
        },

        async loadViewedProgress() {
            if (!this.trackViewProgress || !this.activityId) {
                return;
            }

            try {
                const { data } = await axios.get(`/api/activities/${this.activityId}/slide-deck-progress`);
                const viewed = data?.data?.viewed_slides ?? [];

                viewed.forEach(slideNumber => {
                    const index = Number(slideNumber) - 1;
                    // Clamp to the current deck so stale/out-of-range numbers can't mark it complete.
                    if (Number.isInteger(index) && index >= 0 && index < this.totalSlides) {
                        this.viewedSlides[index] = true;
                    }
                });

                this.emitViewedChange();
            } catch {
                // Progress is advisory; ignore fetch failures.
            }
        },

        syncSliders(index) {
            // Safely synchronize both sliders to the same index
            if (this.$refs.vueperslides1) {
                this.$refs.vueperslides1.goToSlide(index, { emit: false, origin: 'init' });
            }

            if (this.$refs.vueperslides2) {
                this.$refs.vueperslides2.goToSlide(index, { emit: false, origin: 'init' });
            }
        },
    },
});
</script>

<style lang="scss" scoped>
// Filmstrip thumbnails (opt-in via `filmstrip-thumbnails`): a left-aligned row of
// fixed-size thumbnails that scrolls horizontally when the slides overrun the width.
.thumbnail-filmstrip {
    display: flex;
    gap: 6px;
    margin-top: 12px;
    min-width: 0; // let the strip shrink + scroll instead of forcing parents wider
    overflow-x: auto;
    overflow-y: hidden;
    padding-bottom: 4px; // room for the scrollbar so it doesn't overlap the thumbs
    scroll-behavior: smooth;
}

.thumbnail-filmstrip__item {
    position: relative; // anchor the viewed badge
    flex: 0 0 140px;
    height: 135px;
    padding: 0;
    border: 1px solid #e2e8f0;
    background-color: #f1f5f9;
    background-size: cover;
    background-position: center;
    cursor: pointer;
    opacity: 0.85;
    transition:
        opacity 0.15s ease,
        border-color 0.15s ease;

    &:hover {
        opacity: 1;
    }
}

.thumbnail-filmstrip__item--active {
    opacity: 1;
    border: 2px solid var(--primary-color);
}

// Completion indicator overlaid on each viewed thumbnail (top-right green check badge).
.thumbnail-viewed-badge {
    position: absolute;
    top: 4px;
    right: 4px;
    z-index: 2;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 20px;
    height: 20px;
    border-radius: 9999px;
    background-color: #22c55e;
    color: #fff;
    font-size: 11px;
    line-height: 1;
    box-shadow: 0 0 0 2px #fff;
}

.slide-progress {
    margin: 0.5rem 0 0;
    font-size: 0.875rem;
    color: #6b7280;

    &--complete {
        color: #15803d;
        font-weight: 600;
    }
}

// Keep the player from stretching across the full-width corporate content column; cap it at a
// comfortable reading size and align it to the left.
.slide-deck-player {
    max-width: 900px;
    margin-inline: 0;
}

.thumbnails {
    margin: 0.25% auto;

    // Override vueperslides padding-bottom technique to prevent extra space
    // The parallax wrapper uses padding-bottom for aspect ratio, but this creates
    // extra visible space. Instead, let the content define the height.
    :deep(.vueperslides__parallax-wrapper) {
        padding-bottom: 0 !important;
    }

    // Make the track position relative instead of absolute so it takes up space
    :deep(.vueperslides__track) {
        position: relative;
        height: auto;
    }

    // Let slides have natural height based on aspect-ratio CSS
    :deep(.vueperslide) {
        aspect-ratio: 1 / 1;
        height: auto !important;
    }

    &-two-thirds {
        max-width: 66%;
    }

    &-half {
        max-width: 50%;
    }

    &-one-thirds {
        max-width: 33%;
    }
}

.thumbnails .vueperslide {
    box-sizing: border-box;
    border: 1px solid #fff;
    transition: 0.3s ease-in-out;
    opacity: 0.7;
    cursor: pointer;
    background-size: cover;
    background-position: center;
}

.thumbnails .vueperslide--active {
    box-shadow: 0 0 6px rgba(0, 0, 0, 0.3);
    opacity: 1;
    border-color: #000;
}

// Completion indicator overlaid on each viewed thumbnail (top-right green check badge).
.thumbnail-viewed-badge {
    position: absolute;
    top: 4px;
    right: 4px;
    z-index: 2;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 20px;
    height: 20px;
    border-radius: 9999px;
    background-color: #22c55e;
    color: #fff;
    font-size: 11px;
    line-height: 1;
    box-shadow: 0 0 0 2px #fff;
}

.slide-progress {
    margin: 0.5rem 0 0;
    font-size: 0.875rem;
    color: #6b7280;

    &--complete {
        color: #15803d;
        font-weight: 600;
    }
}

.wrapper {
    overflow: hidden !important;
    position: relative !important;
    width: 100%;
}

// Fullscreen: on real mobile (iOS Safari) the native Fullscreen API is not
// available for non-video elements, so vue-fullscreen falls back to a CSS
// `position: fixed` fill. Inside the app DOM that fixed element renders *under*
// the global nav (ancestor stacking/transform context), so the deck tucks
// beneath the navbar. The `teleport` prop moves it to <body> to escape that
// context; here we pin it to the viewport above everything and center the slide
// so the image sits in the middle instead of pinned to the top.
//
// Default fullscreen (thumbnails hidden): keep vueperslides' natural aspect-ratio
// height — overriding parallax/padding here caused a black screen and arrows
// pinned to the top (QA regression after Jul 17).
//
// "Show Slides" layout lives under `.wrapper--fullscreen-nav` only.
.wrapper--fullscreen {
    position: fixed !important;
    inset: 0 !important;
    z-index: 2147483647; // above the global nav bar
    display: flex !important;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    height: 100%;
    width: 100%;
    background: #000;

    // Main slides: full width, natural ratio height, capped to the viewport.
    // No flex-grow — let it keep its height so justify-content can center it.
    :deep(.vueperslides:not(.thumbnails)) {
        position: relative;
        width: 100%;
        max-height: 100vh;
        max-height: 100dvh;
        flex: 0 0 auto;
    }

    // vueperslides anchors arrows at top: 50% of __inner — keep them on the slide sides.
    :deep(.vueperslides:not(.thumbnails) .vueperslides__arrow) {
        top: 50%;
        transform: translateY(-50%);
    }

    :deep(.vueperslides:not(.thumbnails) .vueperslides__arrow--prev) {
        left: 0.5em;
        right: auto;
    }

    :deep(.vueperslides:not(.thumbnails) .vueperslides__arrow--next) {
        right: 0.5em;
        left: auto;
    }

    // Single-image decks (rendered as a plain <img>, not vueperslides).
    :deep(img) {
        max-width: 100%;
        max-height: 100vh;
        max-height: 100dvh;
        object-fit: contain;
    }
}

// Only when "Show Slides" is active: column flex shrinks the main slide above the
// thumbnail strip so the active slide stays visible (especially in landscape).
.wrapper--fullscreen.wrapper--fullscreen-nav {
    justify-content: flex-start;
    align-items: stretch;
    box-sizing: border-box;
    padding: 0.5rem 0;
    padding-bottom: max(0.5rem, env(safe-area-inset-bottom, 0px));
    height: 100vh;
    height: 100dvh;
    max-height: 100vh;
    max-height: 100dvh;
    overflow: hidden !important;

    // Fill remaining viewport above the thumbnail strip. Override vueperslides'
    // padding-bottom aspect-ratio box so flex/max-height actually constrain height
    // (padding-bottom % of width ignores max-height and caused the cut-off).
    :deep(.vueperslides:not(.thumbnails)) {
        flex: 1 1 auto;
        min-height: 0;
        position: relative;
        width: 100%;
        max-height: 100%;

        // __inner collapses when parallax padding-bottom is removed (track is absolute).
        // Pin it to the flex-allocated slide viewport so slides render and arrows center.
        .vueperslides__inner {
            position: absolute;
            inset: 0;
        }

        .vueperslides__parallax-wrapper {
            height: 100% !important;
            max-height: 100%;
            padding-bottom: 0 !important;
        }

        .vueperslides__track {
            position: absolute;
            inset: 0;
            height: 100%;
        }

        .vueperslide {
            height: 100% !important;
            background-size: contain;
            background-repeat: no-repeat;
            background-position: center;
        }

        .vueperslides__arrow {
            top: 50%;
            transform: translateY(-50%);
        }

        .vueperslides__arrow--prev {
            left: 0.5em;
            right: auto;
        }

        .vueperslides__arrow--next {
            right: 0.5em;
            left: auto;
        }
    }

    :deep(img) {
        flex: 1 1 auto;
        min-height: 0;
        max-width: 100%;
        max-height: 100%;
        object-fit: contain;
        align-self: center;
    }

    .thumbnail-filmstrip {
        flex: 0 0 auto;
        width: 100%;
        margin-top: 0.5rem;
        padding-inline: 0.5rem;
        box-sizing: border-box;

        &__item {
            flex-basis: 5.5rem;
            height: 5rem;
        }
    }

    :deep(.thumbnails) {
        flex: 0 0 auto;
        width: 100%;
        max-width: 100%;
        margin: 0.5rem auto 0;

        .vueperslide {
            max-height: 5rem;
        }
    }

    // Landscape phones and tablets (max-height: 500px missed iPad landscape).
    @media (orientation: landscape) {
        .thumbnail-filmstrip__item {
            flex-basis: 4rem;
            height: 3.25rem;
        }

        :deep(.thumbnails .vueperslide) {
            max-height: 3.25rem;
        }
    }

    // Portrait phones / small tablets: keep the strip compact when vertical space is tight.
    @media (orientation: portrait) and (max-height: 900px) {
        .thumbnail-filmstrip__item {
            flex-basis: 4.5rem;
            height: 4rem;
        }

        :deep(.thumbnails .vueperslide) {
            max-height: 4rem;
        }
    }
}

.fullscreen-icon {
    position: absolute;
    top: 5px;
    right: 5px;
    z-index: 1;

    &-navigation {
        right: 60px;
    }
}

.slide {
    background-repeat: no-repeat;
    background-position: center;
}

.fullscreen-slide {
    max-height: 100vh;
    max-height: 100dvh;
    background-size: contain;
    background-repeat: no-repeat;
    background-position: center;
}

.button.is-primary.fullscreen-icon-navigation {
    -webkit-box-shadow: none !important;
    box-shadow: none !important;
}

// Navigation arrows: a semi-transparent dark backdrop behind the library chevron so the arrows keep
// good contrast against any slide background. (Corporate overrides this with its own pill style in
// SlideDeckCorporate.vue — a higher-specificity :deep block — so this only applies to stanbridge and
// other shared-player usages.)
:deep(.vueperslides__arrow) {
    &::before {
        content: '';
        position: absolute;
        top: 0;
        bottom: 0;
        left: 0;
        right: 0;
        background-color: rgba(0, 0, 0, 0.5);
        z-index: -1;
    }

    &:hover::before {
        background-color: rgba(0, 0, 0, 0.7);
    }

    svg {
        stroke: white;
        stroke-width: 2;
    }
}
</style>
